import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import * as _ from 'lodash';

import { LanguageService, UserService } from 'shared/services';
import { FcProducts } from 'types/access';

import { NavigationLink } from './types';
import { getNavigationLinksByFcProducts } from './utils';

@Injectable({
    providedIn: 'root'
})
export class BrandShopNavigationService {
    private _navigationLinks = new BehaviorSubject<NavigationLink[]>([]);
    navigationLinks: Observable<NavigationLink[]>;

    private _activePageTitle = new BehaviorSubject<string>(null);
    activePageTitle: Observable<string>;

    constructor(
        private languageService: LanguageService,
        private userService: UserService
    ) {
        this.activePageTitle = combineLatest([
            this._activePageTitle.asObservable(),
            this.languageService.onLangChange().pipe(startWith(null))
        ]).pipe(
            switchMap(([titleKey]) => (titleKey ? this.languageService.translateObservable(titleKey) : of(''))),
            shareReplay(1)
        );

        this.navigationLinks = this._navigationLinks
            .asObservable()
            .pipe(map((links) => links.sort((a, b) => a?.index - b?.index)));
    }

    /**
     * Set the navigation links based on the FC products that the brand participates in.
     *
     * @param fcProducts {FcProducts}
     */
    setNavigationLinksByFcProducts(brandId: string, fcProducts: FcProducts) {
        const navigationLinks = getNavigationLinksByFcProducts(
            brandId,
            fcProducts,
            this.userService.getOrganizationId()
        );
        this._navigationLinks.next(navigationLinks);
    }

    /**
     * Append additional navigation links on top of the default ones.
     *
     * @param links {NavigationLink[]} - The additional navigation links to add.
     */

    appendLinks(links: NavigationLink[]) {
        const currentLinks = this._navigationLinks.value;
        const newLinks = _.concat(currentLinks, links);

        this._navigationLinks.next(newLinks);
    }

    setPageTitle(titleKey: string) {
        this._activePageTitle.next(titleKey);
    }
}
